import {
  beginMailMutation,
  failMailMutation,
  finishMailMutation,
  setMailMutationLabel,
  setMailMutationPhase,
  setMailMutationStepLabel,
  setMailMutationStepStatus,
} from "@/lib/mail/mailMutationProgress";
import {
  MAIL_INCOMING_PREPARE_EVENT,
  type MailIncomingPrepareDetail,
} from "@/lib/mail/mailIncomingPrepareEvents";
import {
  getPrimaryActiveFolderStorageProgress,
  subscribeFolderStorageProgress,
} from "@/lib/storage/folderStorageProgress";

const PREPARE_STEP_LABELS = [
  "1. En-têtes / objet",
  "2. Préparation du contenu",
  "3. Copie .eml serveur",
] as const;

let prepareJobId: string | null = null;
let headersDone = false;
let bodyDone = false;
let bootstrapped = false;

function ensurePrepareJob(count: number): string {
  if (prepareJobId != null) return prepareJobId;
  const label =
    count > 0
      ? count === 1
        ? "Préparation d’un message"
        : `Préparation de ${count} messages`
      : "Préparation réception";
  prepareJobId = beginMailMutation(label, {
    stepLabels: [...PREPARE_STEP_LABELS],
    kind: "receive",
    keepHistory: true,
  });
  setMailMutationPhase(prepareJobId, "imap-sync");
  headersDone = false;
  bodyDone = false;
  return prepareJobId;
}

function markStepOk(index: number): void {
  if (prepareJobId == null) return;
  setMailMutationStepStatus(prepareJobId, index, "ok");
}

function markStepRunning(index: number): void {
  if (prepareJobId == null) return;
  setMailMutationStepStatus(prepareJobId, index, "running");
}

function tryFinishPrepareJob(): void {
  if (prepareJobId == null) return;
  const archive = getPrimaryActiveFolderStorageProgress();
  if (archive?.active) return;
  if (!headersDone || !bodyDone) return;

  markStepOk(2);
  const id = prepareJobId;
  prepareJobId = null;
  setMailMutationLabel(id, "Préparation terminée");
  finishMailMutation(id);
}

function syncArchiveStep(): void {
  const archive = getPrimaryActiveFolderStorageProgress();

  if (archive?.active) {
    const created = prepareJobId == null;
    const id = ensurePrepareJob(0);
    // Bootstrap archive seul (pas de nouveaux mails) : étapes 1–2 déjà OK.
    if (created) {
      markStepOk(0);
      markStepOk(1);
      headersDone = true;
      bodyDone = true;
    }
    markStepRunning(2);
    const pct = Math.max(0, Math.min(100, Math.round(archive.progress)));
    const detail =
      archive.total > 0
        ? `${archive.done}/${archive.total}`
        : pct > 0
          ? `${pct} %`
          : "file serveur";
    setMailMutationStepLabel(id, 2, `3. Copie .eml serveur (${detail})`);
    setMailMutationLabel(id, archive.label.trim() || "Copie .eml serveur…");
    return;
  }

  if (archive?.error && prepareJobId != null) {
    setMailMutationStepStatus(prepareJobId, 2, "error", archive.error);
    failMailMutation(prepareJobId, archive.error);
    prepareJobId = null;
    return;
  }

  if (prepareJobId != null && headersDone && bodyDone) {
    markStepOk(2);
    tryFinishPrepareJob();
  }
}

function onIncomingPrepare(event: Event): void {
  const detail = (event as CustomEvent<MailIncomingPrepareDetail>).detail;
  const count = Math.max(0, detail?.count ?? 0);
  const phase = detail?.phase ?? "body";

  if (count > 0) {
    const id = ensurePrepareJob(count);
    if (phase === "headers") {
      setMailMutationLabel(
        id,
        count === 1 ? "Réception — en-têtes…" : `Réception — ${count} en-têtes…`
      );
      markStepRunning(0);
      headersDone = false;
      bodyDone = false;
    } else {
      markStepOk(0);
      headersDone = true;
      setMailMutationLabel(
        id,
        count === 1
          ? "Préparation du contenu…"
          : `Préparation du contenu ×${count}`
      );
      markStepRunning(1);
      bodyDone = false;
    }
    syncArchiveStep();
    return;
  }

  if (prepareJobId != null) {
    markStepOk(0);
    headersDone = true;
    markStepOk(1);
    bodyDone = true;
    syncArchiveStep();
    tryFinishPrepareJob();
  }
}

/**
 * Réception → journal en 3 étapes (en-têtes / contenu / .eml).
 * Remplace le spinner bas de sidebar pour la copie DD02.
 */
export function bootstrapMailActivityJournalBridge(): void {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;
  window.addEventListener(MAIL_INCOMING_PREPARE_EVENT, onIncomingPrepare);
  subscribeFolderStorageProgress(() => {
    syncArchiveStep();
  });
}

/** Annule le job « Préparation… » en cours (purge manuelle journal). */
export function abortMailActivityPrepareJob(): void {
  if (prepareJobId == null) {
    headersDone = false;
    bodyDone = false;
    return;
  }
  const id = prepareJobId;
  prepareJobId = null;
  headersDone = false;
  bodyDone = false;
  finishMailMutation(id);
}
